/*
 * Copyright (c) 2024 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain
 * proprietary information of OCLC, Inc. and shall not be disclosed in whole or in
 * any part to any third party or used by any person for any purpose, without written
 * consent of OCLC, Inc.  Duplication of any portion of these  materials shall include this notice.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedDate } from 'react-intl';
import { Grid2 as Grid, Typography } from '@mui/material';
import _ from 'lodash';

import { errorDetails } from '../../utils/messages';
import commonActions from '../../utils/actions';
import { StyledErrorDetails } from './styles';

const FILTERABLE_DETAILS = ['REFERRER', 'PARAMETERS'];

const errorValues = (error, parameters, statusCode) => {
  return {
    STATUS_CODE: statusCode,
    REASON: error,
    REQUEST_DATE: (
      <FormattedDate
        value={new Date()}
        year={'numeric'}
        month={'numeric'}
        day={'numeric'}
        hour={'numeric'}
        minute={'numeric'}
        second={'numeric'}
        timeZoneName={'short'}
      />
    ),
    USER_AGENT: window.navigator.userAgent,
    REQUEST_URI: document.documentURI,
    REFERRER: document.referrer,
    PARAMETERS: parameters,
  };
};

/**
 * Component that builds the ErrorDetailLine.
 * @param messageKey the message key
 * @param messageValue the message value
 * @return {*}
 */
const ErrorDetail = ({ messageKey, messageValue }) => {
  if (FILTERABLE_DETAILS.includes(messageKey) && !messageValue) {
    return null;
  }

  return (
    <Grid id={messageKey} data-testid={messageKey} className='error-detail'>
      <Typography component='dt'>
        {commonActions.getText(errorDetails[messageKey])}
      </Typography>
      <Typography component='dd'>{messageValue}</Typography>
    </Grid>
  );
};

ErrorDetail.propTypes = {
  messageKey: PropTypes.string.isRequired,
  messageValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.node,
  ]),
};

/**
 * Component that builds the ErrorDetails.
 * @param error the error message
 * @param parameters the parameters
 * @param statusCode the status code
 * @return {*}
 */
const ErrorDetails = React.memo(({ error, parameters, statusCode }) => {
  return (
    <StyledErrorDetails component='dl'>
      {_.map(errorValues(error, parameters, statusCode), (value, key) => (
        <ErrorDetail key={key} messageKey={key} messageValue={value} />
      ))}
    </StyledErrorDetails>
  );
});

ErrorDetails.propTypes = {
  error: PropTypes.string,
  parameters: PropTypes.string,
  statusCode: PropTypes.number,
};

export default ErrorDetails;
